'use client';

/* ──────────────────────────────────────────────
 *  GiftCardPaymentSection — paiement Stripe inline
 *  du tunnel carte cadeau (miroir de `BookingPaymentSection`).
 *  Monté dans l'étape « Paiement » une fois le checkout
 *  créé (pro_payment). Reste dans le design `bt-*` : monte
 *  `ProPaymentStripeForm` directement + `PaymentReassurance`,
 *  puis poll le statut jusqu'au règlement. Un moyen à redirection
 *  (Klarna) quitte la page : le tunnel parent reprend le retour
 *  via `useRedirectReturn` (paramètre `redirectReturnParam`).
 * ────────────────────────────────────────────── */

import { useCallback, useEffect, useMemo, useRef, useState, type JSX, type ReactNode } from 'react';
import { ProPaymentStatus } from '@cfixe-monorepo/shared';
import { ProPaymentStripeForm } from '../../pro-payment/components/ProPaymentStripeForm';
import { buildRedirectReturnUrl, type RedirectReturnParam } from '../../pro-payment/redirect-return';
import { PaymentReassurance } from '../../booking/components/shared/PaymentReassurance';
import { getRequiredCaptchaToken, type CapInstance } from '../../website/functions/captcha';
import type { GiftCardCallbacks } from '../api/create-gift-card-callbacks';
import type { ProPaymentStatusResponse } from '../../pro-payment/types';
import '../../pro-payment/styles/pro-payment.scss';

const POLL_INTERVAL_MS = 2_000;
const POLL_TIMEOUT_MS = 30_000;

export interface GiftCardPaymentLabels {
  pay: string;
  secure_payment: string;
  payment_methods_accepted: string;
  error_generic: string;
  finalizing: string;
  /** Affiché quand la confirmation tarde (webhook en retard) : on ne déclare pas le paiement réglé. */
  verifying: string;
  retry: string;
  /** CGV (présents uniquement si le pro a uploadé un PDF de conditions de vente). */
  terms_accept?: string;
  terms_read?: string;
}

export interface GiftCardPaymentSectionProps {
  uuid: string;
  /** Marks the Stripe return URL: the tunnel owning this parameter resumes the payment after a redirect. */
  redirectReturnParam: RedirectReturnParam;
  callbacks: Pick<GiftCardCallbacks, 'onLoadPaymentDetails' | 'onPollPaymentStatus'>;
  stripePublishableKey: string;
  /** Moyens annoncés par le pro, en attendant ceux de l'intent Stripe (réassurance — logos CB/Visa/Mastercard…). */
  allowedPaymentMethods?: string[];
  labels: GiftCardPaymentLabels;
  /**
   * Appelé quand le paiement est réglé (PAYED/AUTHORIZED) → confirmation inline. Reçoit la réponse de
   * statut (ex. `reservation_uuid` d'une course dont le tunnel ne connaît que le paiement).
   */
  onPaid: (result: ProPaymentStatusResponse) => void;
  /**
   * `true` tant qu'un paiement est en cours de confirmation (Stripe, puis vérification backend) : le
   * parent ne doit ni annuler ni recréer le paiement pendant ce temps (il est peut-être déjà encaissé).
   */
  onBusyChange?: (busy: boolean) => void;
  /** CGV : si défini, une case d'acceptation obligatoire est affichée et bloque le bouton « Payer ». */
  termsPdfUrl?: string;
  termsAccepted?: boolean;
  onTermsAcceptedChange?: (accepted: boolean) => void;
}

export function GiftCardPaymentSection({
  uuid,
  redirectReturnParam,
  callbacks,
  stripePublishableKey,
  allowedPaymentMethods,
  labels,
  onPaid,
  onBusyChange,
  termsPdfUrl,
  termsAccepted,
  onTermsAcceptedChange,
}: GiftCardPaymentSectionProps): JSX.Element {
  const [clientSecret, setClientSecret] = useState<string | undefined>(undefined);
  const [connectAccountId, setConnectAccountId] = useState<string | undefined>(undefined);
  /* Méthodes de l'intent Stripe lui-même : une empreinte / sauvegarde de carte n'accepte que la carte. */
  const [paymentMethodTypes, setPaymentMethodTypes] = useState<string[] | undefined>(undefined);
  const [amount, setAmount] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  // Le poll a dépassé le délai sans statut réglé : on affiche un état « vérification » + bouton réessayer,
  // sans jamais déclarer le paiement réglé tant que le back n'a pas confirmé PAYED/AUTHORIZED.
  const [verifying, setVerifying] = useState(false);
  const [stripeProcessing, setStripeProcessing] = useState(false);
  const busy = stripeProcessing || finalizing || verifying;
  useEffect((): void => {
    onBusyChange?.(busy);
  }, [busy, onBusyChange]);
  // Incrémenté par le bouton « Réessayer » : relance l'effet de chargement (captcha PoW + détails).
  const [retryKey, setRetryKey] = useState(0);

  const capRef = useRef<CapInstance | null>(null);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);

  useEffect((): (() => void) => {
    mountedRef.current = true;
    return (): void => {
      mountedRef.current = false;
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
    };
  }, []);

  /* ── Détails du paiement (client_secret Stripe + compte Connect), captcha résolu en amont. ── */
  useEffect((): (() => void) => {
    let cancelled = false;
    setLoading(true);
    setLoadError(false);
    void (async (): Promise<void> => {
      try {
        // Le token captcha Cap.js est à usage unique ; on réinitialise l'instance entre deux tentatives
        // pour forcer un nouveau PoW (sinon un échec transitoire condamnerait définitivement le paiement).
        if (retryKey > 0) capRef.current?.reset();
        const token = await getRequiredCaptchaToken(capRef);
        const details = await callbacks.onLoadPaymentDetails(uuid, token);
        if (cancelled) return;
        setClientSecret(details.client_secret);
        setConnectAccountId(details.stripe_connect_account_id);
        setPaymentMethodTypes(details.payment_method_types);
        setAmount(details.amount);
      } catch {
        if (!cancelled) setLoadError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return (): void => {
      cancelled = true;
    };
  }, [uuid, callbacks, retryKey]);

  /* ── Après confirmation Stripe : poll jusqu'au règlement (le webhook peut être en retard). ── */
  const handleStripeSuccess = useCallback((): void => {
    setVerifying(false);
    setFinalizing(true);
    const start = Date.now();
    // Le timeout n'est PAS un succès : Stripe a accepté la carte, mais tant que le back n'a pas
    // confirmé PAYED/AUTHORIZED on ne déclenche pas onPaid(). On bascule sur un état « vérification »
    // (avec bouton réessayer) et on arrête proprement le poll.
    const stopAndVerify = (): void => {
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
      setFinalizing(false);
      setVerifying(true);
    };
    const tick = (): void => {
      callbacks
        .onPollPaymentStatus(uuid)
        .then((res): void => {
          if (!mountedRef.current) return;
          const settled = res.status === ProPaymentStatus.PAYED || res.status === ProPaymentStatus.AUTHORIZED;
          if (settled) {
            onPaid(res);
            return;
          }
          if (Date.now() - start > POLL_TIMEOUT_MS) {
            stopAndVerify();
            return;
          }
          pollTimeoutRef.current = setTimeout(tick, POLL_INTERVAL_MS);
        })
        .catch((): void => {
          if (!mountedRef.current) return;
          if (Date.now() - start > POLL_TIMEOUT_MS) {
            stopAndVerify();
            return;
          }
          pollTimeoutRef.current = setTimeout(tick, POLL_INTERVAL_MS);
        });
    };
    pollTimeoutRef.current = setTimeout(tick, POLL_INTERVAL_MS);
  }, [uuid, callbacks, onPaid]);

  // « Réessayer » depuis l'état vérification : relance un cycle de poll (le statut a pu se régler entre-temps).
  const handleRetryVerify = useCallback((): void => {
    handleStripeSuccess();
  }, [handleStripeSuccess]);

  const returnUrl = useMemo(
    (): string | undefined => buildRedirectReturnUrl(redirectReturnParam, uuid),
    [redirectReturnParam, uuid],
  );

  /* ── Render ────────────────────────────────── */

  if (finalizing) {
    return (
      <div className="bt-checkout__stripe-slot bt-payment-finalizing">
        <span className="bt-spinner bt-spinner--inline" />
        <span>{labels.finalizing}</span>
      </div>
    );
  }

  if (verifying) {
    // Le paiement a été soumis mais la confirmation tarde : on n'affirme rien et on propose de revérifier.
    return (
      <div className="bt-checkout__stripe-slot">
        <div className="bt-payment-finalizing">
          <span>{labels.verifying}</span>
        </div>
        <button type="button" className="bt-cta bt-cta--mt" onClick={handleRetryVerify}>
          {labels.retry}
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bt-checkout__stripe-slot bt-payment-finalizing">
        <span className="bt-spinner bt-spinner--inline" />
      </div>
    );
  }

  if (loadError || !clientSecret || !connectAccountId || !stripePublishableKey) {
    // Le checkout (pro_payment) est déjà créé et le bouton retour masqué : on offre un retry inline
    // pour ne pas bloquer l'utilisateur sur un échec transitoire (captcha PoW, chargement des détails).
    return (
      <div className="bt-checkout__stripe-slot">
        <p className="bt-error">{labels.error_generic}</p>
        <button
          type="button"
          className="bt-cta bt-cta--mt"
          onClick={(): void => {
            capRef.current?.reset();
            setRetryKey((k): number => k + 1);
          }}
        >
          {labels.retry}
        </button>
      </div>
    );
  }

  const payLabel = amount ? `${labels.pay} · ${amount}` : labels.pay;

  const extraContent: ReactNode = (
    <>
      <PaymentReassurance
        methods={paymentMethodTypes ?? allowedPaymentMethods}
        secureLabel={labels.secure_payment}
        methodsLabel={labels.payment_methods_accepted}
      />
      {/* CGV : case d'acceptation obligatoire au moment de payer (même UI que les autres cases). */}
      {termsPdfUrl && (
        <div className="bt-field bt-field--checkbox">
          <label className="bt-field__checkbox-label" htmlFor="gc-terms-pay">
            <input
              id="gc-terms-pay"
              type="checkbox"
              className="bt-field__checkbox"
              checked={Boolean(termsAccepted)}
              onChange={(e): void => onTermsAcceptedChange?.(e.target.checked)}
            />
            <span>
              {labels.terms_accept}{' '}
              <a
                href={termsPdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="bt-terms__link"
                onClick={(e): void => e.stopPropagation()}
              >
                {labels.terms_read}
              </a>
            </span>
          </label>
        </div>
      )}
    </>
  );

  return (
    <div className="bt-checkout__stripe-slot">
      <ProPaymentStripeForm
        clientSecret={clientSecret}
        stripePublishableKey={stripePublishableKey}
        stripeConnectAccountId={connectAccountId}
        payLabel={payLabel}
        errorLabel={labels.error_generic}
        extraContent={extraContent}
        disabled={Boolean(termsPdfUrl) && !termsAccepted}
        returnUrl={returnUrl}
        onSuccess={handleStripeSuccess}
        onProcessingChange={setStripeProcessing}
      />
    </div>
  );
}
